import type { FieldValues } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'

type PasswordStrengthLevel = {
	label: string
	color: string
}

interface PasswordStrengthMeterProps {
	name?: string
	containerClassName?: string
	emptyColor?: string
	levels?: PasswordStrengthLevel[]
	improvementHint?: string
}

const DEFAULT_LEVELS: PasswordStrengthLevel[] = [
	{ label: 'Weak', color: '#f87171' },
	{ label: 'Fair', color: '#fb923c' },
	{ label: 'Good', color: '#8bd450' },
	{ label: 'Strong', color: '#2fd47a' },
]

const scorePassword = (password: string, levels: PasswordStrengthLevel[]) => {
	if (!password) return 0
	let score = 0
	if (password.length >= 8) score++
	if (password.length >= 12) score++
	if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++
	if (/\d/.test(password)) score++
	if (/[^A-Za-z0-9]/.test(password)) score++
	return Math.min(score, levels.length) - 1
}

const PasswordStrengthMeter = ({
	name = 'password',
	containerClassName = 'mt-2',
	emptyColor = '#5a211d',
	levels = DEFAULT_LEVELS,
	improvementHint = ' - try adding numbers, symbols, or a longer phrase',
}: PasswordStrengthMeterProps) => {
	const { watch } = useFormContext<FieldValues>()
	const passwordValue = watch(name)
	const password = typeof passwordValue === 'string' ? passwordValue : ''
	if (!password) return null

	const levelIndex = Math.max(0, scorePassword(password, levels))
	const level = levels[levelIndex]
	const percent = ((levelIndex + 1) / levels.length) * 100

	return (
		<div className={containerClassName}>
			<div className="flex h-1.5 w-full gap-1 overflow-hidden">
				{levels.map((segment, idx) => (
					<div
						key={segment.label}
						className="h-full flex-1 rounded-full transition-colors duration-300"
						style={{ backgroundColor: idx <= levelIndex ? level.color : emptyColor }}
					/>
				))}
			</div>
			<p className="mt-1.5 text-xs font-medium" style={{ color: level.color }}>
				{level.label} password{percent < 100 ? improvementHint : ''}
			</p>
		</div>
	)
}

export default PasswordStrengthMeter
